import { useMutation } from "react-query";
import { axios } from "../../lib/request";
import externalAxios from "axios";
import { createStickerBlob } from "../../lib/stickerify";

const readFileURL = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

const uploadBlobToSignedMedia = async ({ blob, extension = "png", contentType = "image/png" }) => {
    const response = await axios.get(`${process.env.REACT_APP_API_SIGN_URL}/${extension}`);
    const { data } = response || {};
    const options = { headers: { "Content-Type": contentType } };

    await externalAxios.put(data.signature, blob, options);

    const [uuid, signedExtension] = data.name.split(".");

    return {
        uuid,
        extension: signedExtension,
        mediaUrl: data?.uri || data?.name,
    };
};

const uploadFileToSignedMedia = async (file) => {
    const fileURL = await readFileURL(file);
    const [ext] = file.name.split(".").reverse();
    const response = await axios.get(`${process.env.REACT_APP_API_SIGN_URL}/${ext.toLocaleLowerCase()}`);
    const { data } = response || {};
    const options = { headers: { "Content-Type": file.type } };

    await externalAxios.put(data.signature, file, options);

    const [uuid, extension] = data.name.split(".");

    return {
        uuid,
        extension,
        mediaUrl: data?.uri || data?.name || fileURL,
    };
};

const createStickerMedia = async (sourceUrl) => {
    const stickerBlob = await createStickerBlob(sourceUrl);

    return uploadBlobToSignedMedia({
        blob: stickerBlob,
        extension: "png",
        contentType: "image/png",
    });
};

export const useUploadImageMutation = ({
    useQuery,
    name,
    stickerify = false,
    storageMode = "field",
    assetName,
    mediaPrefix,
}) => {
    const [collection, setCollection] = useQuery();

    const commitMedia = async ({ uuid, extension, mediaUrl }) => {
        if (storageMode === "asset") {
            await setCollection({
                assets: {
                    ...collection.assets,
                    photos: [
                        ...(collection.assets?.photos || []).filter(asset => asset.name !== assetName),
                        {
                            name: assetName,
                            url: mediaUrl,
                        },
                    ],
                },
                [`${mediaPrefix}_media_uuid`]: uuid,
                [`${mediaPrefix}_media_extension`]: extension
                    ? extension.toLocaleLowerCase()
                    : extension,
            });
            return;
        }

        // Get existing URLs from the collection
        const existingValue = collection?.[name] || '';
        const existingUrls = existingValue ? existingValue.split(',').filter(url => url.trim()) : [];

        // Add the new URL to the list
        const updatedUrls = [...existingUrls, mediaUrl];

        // Update the collection with comma-delimited URLs
        await setCollection({
            [name]: updatedUrls.join(','),
        });
    };

    const { isLoading, mutateAsync } = useMutation(async (file) => {
        const uploadedMedia = await uploadFileToSignedMedia(file);
        const finalMedia = stickerify
            ? await createStickerMedia(uploadedMedia.mediaUrl)
            : uploadedMedia;

        await commitMedia(finalMedia);

        return finalMedia;
    });

    const { isLoading: isGenerating, mutateAsync: createAiImage } = useMutation(async (prompt) => {
        const response = await axios.post(process.env.REACT_APP_API_GENERATE_URL, { prompt, generate_type: 1 });
        const { data } = response || {};
        const sourceUrl = data?.url;
        const finalMedia = stickerify
            ? await createStickerMedia(sourceUrl)
            : {
                uuid: data?.uuid,
                extension: data?.extension,
                mediaUrl: sourceUrl,
            };

        await commitMedia(finalMedia);

        return finalMedia;
    });

    return {
        isLoading,
        isGenerating,
        mutateAsync,
        createAiImage,
    };
};
